/**
 * player.command: the way the game changes its canonical state on the server (design D1; stage 2 contracts 1-3).
 *
 * For the authenticated player, under the per-player profile lock (commands of one player are serialized; several
 * tabs never CONFLICT, every response carries the current canonical state):
 *  1. The profile document is loaded. An `opId` found in its journal is answered from the journal (a retry after a
 *     lost reply, also after a server restart): the op is never applied twice and random draws are never repeated.
 *     The replay equals the original response while no later change touched the state; after later changes it
 *     carries the current state and revision (a client must never adopt an older state). A journaled opId with a
 *     different { op, args } is BAD_REQUEST 'op_id_reused'. The journal keeps the last config.commands.journalSize
 *     opIds (older ones count as new).
 *  2. The op runs through the shared rules (src/game/rules) in a RuleContext of the server: server time (unix seconds
 *     of the injected clock), the player's stored time zone, the server RNG (ServiceDeps.ruleRng; nothing about it is
 *     sent to the client) and the player's server state (withServerState). Client-callable ops: every RULE_OPS op and
 *     the registered service ops (registerServiceOp); a SERVER_RULE_OPS name or an unknown op is BAD_REQUEST
 *     'unknown_op'.
 *  3. A rule failure (RuleError) is VALIDATION_FAILED { reason, details }. It is journaled too, so a retry gets the
 *     same answer, but it changes neither the state nor the revision.
 *  4. Success: the new game state, the new server state (an op's `result.serverState`, never sent to the client), the
 *     journal entry, the time zone, the next revision and the summary / team projection of the new canonical state
 *     are stored in ONE document write (together with the op's other documents, if it has any, in one atomic storage
 *     batch: ServiceOpOutcome.commit); the other connections of the player get profile.changed { revision }.
 *
 * Service ops (registerServiceOp) wrap SERVER_RULE_OPS whose inputs only the server may supply (present.claim,
 * tribute.claim, grave.search, battle.*): the handler builds the server-side arguments from its own state and runs
 * rule ops through ServiceOpContext.execute, or returns a plan { rulesOp, args } for a single op.
 * Registered here: settings.set and present.claim (the constructor), tribute.claim (tributeClaimOp, wired by the
 * backend with the assault service). The battle ops and grave.search: ./battles.ts (BattleService.registerOps).
 */
import { sha256Hex } from '../../../shared/crypto/sha256';
import { type JsonObject, type JsonValue, cloneJson, isPlainObject } from '../../../shared/json';
import { BackendError } from '../../../shared/protocol/errors';
import type { PayloadOf, ResultOf } from '../../../shared/protocol/messages';
import type { CommandVolatile, PlayerId, PlayerProfile, TributeClaimCommandResult } from '../../../shared/protocol/model';
import { COMMAND_OP_PATTERN } from '../../../shared/protocol/validators';
import { canonicalJson } from '../../../session/hash';
import { type OpRegistry, RULE_OPS, type RuleContext, RuleError, SERVER_RULE_OPS, type StoredGameState, argsObject, isRuleError } from '../../../game/rules/index';
import type { TributeResult as TributeRuleResult } from '../../../game/rules/ops/progress-claims';
import type { RandomSource } from '../../../shared/random';
import type { ConnectionContext } from '../hub';
import { type ServerState, decideTimeZone, ruleFailure, runRulesOp, serverRuleContext } from '../rules-runtime';
import type { StorageBatchOp } from '../storage';
import type { TributeFacts } from './assault';
import type { AccountRecord, CommandJournalEntry, ProfileRecord, ServiceDeps, StoredTimeZone } from './deps';
import type { EconomySource } from './economy';
import { PRESENT_KEY, type ProfileService, pendingPresent } from './profile';

/** What a service op handler works with: the command's server-side view of the player. */
export interface ServiceOpContext {
  readonly playerId: PlayerId;
  readonly account: AccountRecord;
  /** Server time of the command, epoch ms. */
  readonly nowMs: number;
  /** Server time of the command, unix seconds (RuleContext.now). */
  readonly now: number;
  /** The player's calendar, minutes east of UTC. */
  readonly tzOffsetMin: number;
  /** The canonical game state as the command has changed it so far. */
  readonly gameState: StoredGameState;
  /** The server state as the command has changed it so far (a private copy of the document's). */
  readonly serverState: ServerState;
  /** A RuleContext of the command (for direct rule calls): server time, calendar, server RNG, current server state. */
  ruleContext(): RuleContext;
  /**
   * Runs a rule op on `gameState` (default registry SERVER_RULE_OPS: the handler passes server-trusted arguments) and
   * adopts its new state and server state. Returns the op's result without `serverState`. Throws RuleError.
   */
  execute(op: string, args: unknown, registry?: OpRegistry): JsonValue;
  setGameState(state: StoredGameState): void;
  setServerState(state: ServerState): void;
  /** Applies the time zone rule (rules-runtime decideTimeZone) to a reported offset; returns the one in effect. */
  reportTimeZone(offsetMin: number): number;
}

/** A handler's answer: the result for the client (and optional volatile facts / work after the write). */
export interface ServiceOpOutcome {
  result: JsonValue;
  volatile?: CommandVolatile;
  /**
   * Stores the command itself, for an op whose other documents must be written in the SAME atomic storage batch as the
   * profile document (battle.finish: its replay and a zone capture). It must call `write(extra)` once: the profile
   * document (new state, server state, journal entry) plus `extra` in one batch. It may complete `result` before that
   * (the journaled result is taken at the write). Throwing before `write` stores nothing (the command is not journaled).
   * Without it the command writes the profile document alone.
   */
  commit?: (write: (extra: StorageBatchOp[]) => Promise<void>) => Promise<void>;
  /**
   * Runs after the command was stored and before the response is sent (e.g. a zone capture, a replay). The command
   * stays applied when it fails: the error is logged, the response is the journaled one.
   */
  afterCommit?: () => Promise<void> | void;
  /**
   * What the income attribution (./economy.ts) records of the stored change besides the op and the opId: a battle's
   * id, verdict and replay (battle.finish).
   */
  source?: Pick<EconomySource, 'battleId' | 'verdict' | 'replayId'>;
}

/**
 * What the anti-cheat hears of player.command besides the stored changes (CommandService.onSignal): a command
 * answered from the journal (`journal_replay`: a retry of the same opId), a refused command (`refused`: a rule
 * precondition failed; journaled), an opId reused for another command (`op_id_reused`).
 */
export interface CommandSignal {
  kind: 'journal_replay' | 'refused' | 'op_id_reused';
  playerId: PlayerId;
  op: string;
  opId: string;
  /** SHA-256 of { op, args }. */
  hash: string;
  at: number;
  /** refused: the rule's reason, and for 'bad_args' the field and the problem. */
  reason?: string;
  field?: string;
  problem?: string;
}

export type CommandSignalListener = (s: CommandSignal) => void;

/** Shorthand answer: run one rule op with server-built arguments (default registry SERVER_RULE_OPS). */
export interface ServiceOpPlan {
  rulesOp: string;
  args: unknown;
  registry?: OpRegistry;
  volatile?: CommandVolatile;
}

/**
 * A client-callable op the service implements itself. `args` are the client's (validated only as bounded JSON):
 * the handler checks them strictly (RuleError('bad_args') -> VALIDATION_FAILED) and builds the rule op's arguments
 * from the server's own state. A RuleError is a journaled VALIDATION_FAILED; any other error is not journaled.
 */
export type ServiceOpHandler = (ctx: ServiceOpContext, args: JsonObject) => ServiceOpOutcome | ServiceOpPlan | Promise<ServiceOpOutcome | ServiceOpPlan>;

function isPlan(v: ServiceOpOutcome | ServiceOpPlan): v is ServiceOpPlan {
  return typeof (v as ServiceOpPlan).rulesOp === 'string';
}

class OpContext implements ServiceOpContext {
  gameState: StoredGameState;
  serverState: ServerState;
  tzChanged = false;

  constructor(
    readonly playerId: PlayerId,
    readonly account: AccountRecord,
    readonly nowMs: number,
    gameState: StoredGameState,
    serverState: ServerState,
    public tz: StoredTimeZone | undefined,
    private readonly random: RandomSource,
  ) {
    this.gameState = gameState;
    this.serverState = serverState;
  }

  get now(): number {
    return Math.floor(this.nowMs / 1000);
  }

  get tzOffsetMin(): number {
    return decideTimeZone(this.tz, undefined, this.nowMs).offsetMin;
  }

  ruleContext(): RuleContext {
    return serverRuleContext({ nowMs: this.nowMs, tzOffsetMin: this.tzOffsetMin, random: this.random, serverState: this.serverState });
  }

  execute(op: string, args: unknown, registry: OpRegistry = SERVER_RULE_OPS): JsonValue {
    const ran = runRulesOp(op, this.gameState, args, this.ruleContext(), registry);
    this.gameState = ran.gameState;
    if (ran.serverState) this.serverState = ran.serverState;
    return ran.result;
  }

  setGameState(state: StoredGameState): void {
    this.gameState = cloneJson(state);
  }

  setServerState(state: ServerState): void {
    this.serverState = cloneJson(state);
  }

  reportTimeZone(offsetMin: number): number {
    const d = decideTimeZone(this.tz, offsetMin, this.nowMs);
    if (d.changed) {
      this.tz = d.stored;
      this.tzChanged = true;
    }
    return d.offsetMin;
  }
}

/**
 * settings.set: the rule op, plus the device time zone the command service stores (rule: once per 24 h). The result's
 * and the volatile `tzOffsetMin` are the offset IN EFFECT after the request (the stored one when the change was
 * refused), never just the requested one.
 */
const settingsSet: ServiceOpHandler = (ctx, args) => {
  const result = ctx.execute('settings.set', args, RULE_OPS);
  const tz = args.tzOffsetMin;
  if (typeof tz !== 'number') return { result };
  const tzOffsetMin = ctx.reportTimeZone(tz);
  return { result: isPlainObject(result) ? { ...result, tzOffsetMin } : result, volatile: { tzOffsetMin } };
};

/**
 * present.claim {}: the daily present the session entry of profile.load found due (serverState[PRESENT_KEY], see
 * ProfileService) -> SERVER op present.claim { available }; the flag is consumed, so the present is claimed once.
 * Without a due present (or before the tutorial is closed): VALIDATION_FAILED 'not_available'.
 */
export const presentClaimOp: ServiceOpHandler = (ctx, args) => {
  argsObject(args, []);
  const pending = pendingPresent(ctx.serverState);
  const result = ctx.execute('present.claim', { available: pending !== null });
  const next = { ...ctx.serverState };
  delete next[PRESENT_KEY];
  // Today's claimed present, for the opt-in ad that doubles it after the claim (services/ads.ts, ad_daily_gift_x2).
  if (pending && isPlainObject(result)) next[LAST_PRESENT_KEY] = { day: pending.day, ...(result as JsonObject) };
  ctx.setServerState(next);
  return { result };
};

/** serverState key of the last claimed daily present: { day, tween, gold, diam, skill_pts }. */
export const LAST_PRESENT_KEY = 'lastPresent';

/** Where tribute.claim gets the assault facts (AssaultService.tributeFacts). */
export interface TributeFactsSource {
  tributeFacts(account: AccountRecord): Promise<TributeFacts>;
}

/**
 * tribute.claim {}: SERVER op tribute.claim with the zones (owners, last captures), the player's clan members among
 * the owners and the server time (design D7): the gold goes into the canonical state, and the only cooldown is the
 * state's next_assault_payment (VALIDATION_FAILED 'not_available' before it, or when there is nothing to collect).
 * Public times are epoch ms (design D9): the rule's next_assault_payment (unix seconds) is `nextPaymentAt` in the
 * result and in the details of 'not_available'.
 */
export function tributeClaimOp(source: TributeFactsSource): ServiceOpHandler {
  return async (ctx, args) => {
    argsObject(args, []);
    const facts = await source.tributeFacts(ctx.account);
    let r: TributeRuleResult;
    try {
      r = ctx.execute('tribute.claim', { playerId: ctx.playerId, zones: facts.zones, clanMembers: facts.clanMembers, serverTime: ctx.now }) as unknown as TributeRuleResult;
    } catch (err) {
      if (isRuleError(err) && typeof err.details.next_assault_payment === 'number') {
        const { next_assault_payment: nextS, ...details } = err.details;
        throw new RuleError(err.reason, { ...details, nextPaymentAt: (nextS as number) * 1000 });
      }
      throw err;
    }
    const { next_assault_payment: nextS, ...rest } = r;
    const result: TributeClaimCommandResult = { ...rest, nextPaymentAt: nextS * 1000 };
    return { result: result as unknown as JsonValue };
  };
}

export class CommandService {
  private readonly serviceOps = new Map<string, ServiceOpHandler>();
  private readonly signalListeners = new Set<CommandSignalListener>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly profiles: ProfileService,
  ) {
    this.registerServiceOp('settings.set', settingsSet);
    this.registerServiceOp('present.claim', presentClaimOp);
  }

  /**
   * Makes `name` a client-callable command implemented by `handler` (it takes precedence over a RULE_OPS op of the
   * same name). Returns the unregister function.
   */
  registerServiceOp(name: string, handler: ServiceOpHandler): () => void {
    if (!COMMAND_OP_PATTERN.test(name)) throw new Error(`registerServiceOp: bad op name "${name}"`);
    this.serviceOps.set(name, handler);
    return () => {
      if (this.serviceOps.get(name) === handler) this.serviceOps.delete(name);
    };
  }

  /** Calls `l` with every CommandSignal (the anti-cheat). Returns the unsubscribe. */
  onSignal(l: CommandSignalListener): () => void {
    this.signalListeners.add(l);
    return () => this.signalListeners.delete(l);
  }

  private signal(s: CommandSignal): void {
    for (const l of this.signalListeners) {
      try {
        l(s);
      } catch (err) {
        this.d.logger.error('command signal listener failed', { playerId: s.playerId, err });
      }
    }
  }

  /** Every op a client may send: the RULE_OPS ops and the registered service ops. */
  clientOps(): string[] {
    return [...new Set([...Object.keys(RULE_OPS), ...this.serviceOps.keys()])].sort();
  }

  private handlerOf(op: string): ServiceOpHandler {
    const h = this.serviceOps.get(op);
    if (h) return h;
    if (Object.prototype.hasOwnProperty.call(RULE_OPS, op)) return (ctx, args) => ({ result: ctx.execute(op, args, RULE_OPS) });
    throw new BackendError('BAD_REQUEST', `Unknown command "${op}"`, { reason: 'unknown_op', op });
  }

  async execute(playerId: PlayerId, p: PayloadOf<'player.command'>, conn: ConnectionContext | null): Promise<ResultOf<'player.command'>> {
    const handler = this.handlerOf(p.op);
    const hash = sha256Hex(canonicalJson({ op: p.op, args: p.args }));
    return this.profiles.withRecord(playerId, async (rec, a) => {
      const journal = rec.journal ?? [];
      const hit = journal.find((e) => e.opId === p.opId);
      if (hit) {
        this.signal({ kind: hit.hash === hash ? 'journal_replay' : 'op_id_reused', playerId, op: p.op, opId: p.opId, hash, at: this.d.clock.now() });
        return this.replay(hit, hash, rec);
      }
      if (rec.profile.gameState === null) throw new BackendError('INVALID_STATE', 'The profile has no game state yet: load it first', { reason: 'no_profile' });

      const nowMs = this.d.clock.now();
      // Private copies: a failed command must leave nothing behind in the document it journals.
      const ctx = new OpContext(playerId, a, nowMs, cloneJson(rec.profile.gameState) as unknown as StoredGameState, cloneJson(rec.serverState ?? {}), rec.tz, this.d.ruleRng);
      let outcome: ServiceOpOutcome;
      try {
        const out = await handler(ctx, p.args);
        outcome = isPlan(out) ? { result: ctx.execute(out.rulesOp, out.args, out.registry ?? SERVER_RULE_OPS), ...(out.volatile ? { volatile: out.volatile } : {}) } : out;
      } catch (err) {
        if (!isRuleError(err)) throw err;
        const failure = ruleFailure(p.op, err);
        rec.journal = this.trim([...journal, { opId: p.opId, hash, op: p.op, at: nowMs, ok: false, error: failure.toProtocol() }]);
        await this.profiles.storeRecord(rec, a, false);
        const d = err.details as { field?: unknown; problem?: unknown };
        this.signal({
          kind: 'refused',
          playerId,
          op: p.op,
          opId: p.opId,
          hash,
          at: nowMs,
          reason: err.reason,
          ...(typeof d.field === 'string' ? { field: d.field } : {}),
          ...(typeof d.problem === 'string' ? { problem: d.problem.slice(0, 300) } : {}),
        });
        throw failure;
      }

      const stored: { entry?: CommandJournalEntry; profile?: PlayerProfile } = {};
      const write = async (extra: StorageBatchOp[]): Promise<void> => {
        if (stored.entry) throw new Error(`${p.op}: the command was already stored`);
        const entry: CommandJournalEntry = { opId: p.opId, hash, op: p.op, at: nowMs, ok: true, revision: rec.profile.revision + 1, result: cloneJson(outcome.result) ?? null };
        if (outcome.volatile && isPlainObject(outcome.volatile)) entry.volatile = cloneJson(outcome.volatile);
        stored.entry = entry;
        rec.journal = this.trim([...journal, entry]);
        rec.serverState = ctx.serverState;
        if (ctx.tzChanged) rec.tz = ctx.tz;
        delete rec.fresh;
        stored.profile = await this.profiles.commitCanonical(rec, a, ctx.gameState, nowMs, conn, extra, { kind: 'command', op: p.op, ref: p.opId, ...(outcome.source ?? {}) });
      };
      if (outcome.commit) await outcome.commit(write);
      else await write([]);
      const { entry, profile } = stored;
      if (!entry || !profile) throw new Error(`${p.op}: the command handler did not store the command`);
      if (outcome.afterCommit) {
        try {
          await outcome.afterCommit();
        } catch (err) {
          this.d.logger.error('command follow-up failed', { playerId, op: p.op, err });
        }
      }
      return this.response(entry, profile.revision, profile.gameState);
    });
  }

  private trim(journal: CommandJournalEntry[]): CommandJournalEntry[] {
    const size = Math.max(1, this.d.config.commands.journalSize);
    return journal.length > size ? journal.slice(journal.length - size) : journal;
  }

  private response(entry: CommandJournalEntry, revision: number, gameState: JsonValue | null): ResultOf<'player.command'> {
    const r: ResultOf<'player.command'> = { revision, gameState: gameState ?? null, result: entry.result ?? null, serverTime: entry.at };
    if (entry.volatile) r.volatile = entry.volatile;
    return r;
  }

  private replay(hit: CommandJournalEntry, hash: string, rec: ProfileRecord): ResultOf<'player.command'> {
    if (hit.hash !== hash) throw new BackendError('BAD_REQUEST', 'This opId was used for another command', { reason: 'op_id_reused', opId: hit.opId });
    if (!hit.ok) throw BackendError.fromProtocol(hit.error ?? { code: 'VALIDATION_FAILED', message: `${hit.op}: rejected`, details: { reason: 'command_refused' } });
    return this.response(hit, rec.profile.revision, rec.profile.gameState);
  }
}
